/**
 * Phase 8: Upper 4 buttons - CAM Views, OUTPUT Views, REMOTE Views, MEDIA Shares.
 * Each opens a modal with grid view of tracks for that category.
 */

import { useState } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Grid, SvgIcon } from '@mui/material';
import { Close, Videocam, ScreenShare, PermMedia } from '@mui/icons-material';
import { MdOutlineScreenShare } from 'react-icons/md';
import type { FlowDto, FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import { getEntityRefFromFlow } from '@/helpers/entity-ref.helper';
import { getObjectId } from '@/helpers/object.id.helper';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key.tsx';
import { PerformCategoryModalContent } from './perform-category-modal-content';

export type PerformCategory = 'cam' | 'output' | 'remote' | 'media' | 'speakers';

export interface PerformUpperButtonsProps {
  flow: FlowDto;
  selectedSlot?: FlowSlotDto;
  eventSlotId?: string;
  livekitRoomId?: string;
  liveContextState?: LiveContextStateDto;
}

const CATEGORIES: { id: PerformCategory; labelKey: TranslationKey; defaultLabel: string; icon: React.ReactNode }[] = [
  {
    id: 'cam',
    labelKey: 'flows.detail.perform.upperButtons.cam' as TranslationKey,
    defaultLabel: 'Local Cameras',
    icon: <Videocam />,
  },
  {
    id: 'output',
    labelKey: 'flows.detail.perform.upperButtons.output' as TranslationKey,
    defaultLabel: 'OUTPUT Views',
    icon: <ScreenShare />,
  },
  {
    id: 'remote',
    labelKey: 'flows.detail.perform.upperButtons.remote' as TranslationKey,
    defaultLabel: 'Media Laptops',
    icon: <SvgIcon component={MdOutlineScreenShare} inheritViewBox />,
  },
  {
    id: 'media',
    labelKey: 'flows.detail.perform.upperButtons.media' as TranslationKey,
    defaultLabel: 'MEDIA Shares',
    icon: <PermMedia />,
  },
];

export function PerformUpperButtons({
  flow,
  selectedSlot,
  eventSlotId,
  livekitRoomId,
  liveContextState,
}: PerformUpperButtonsProps) {
  const t = useTranslations();
  const [openCategory, setOpenCategory] = useState<PerformCategory | null>(null);
  const flowId = flow?.origin ? getObjectId(flow.origin) : flow?.id;
  const entityRef = flowId ? getEntityRefFromFlow(flowId, flow?.liveContext) : undefined;
  const openCategoryMeta = CATEGORIES.find((c) => c.id === openCategory);
  const openCategoryTitle = openCategoryMeta
    ? t(openCategoryMeta.labelKey, { defaultValue: openCategoryMeta.defaultLabel })
    : '';

  return (
    <Box sx={{ py: 1 }}>
      <Grid container spacing={1}>
        {CATEGORIES.map(({ id, labelKey, defaultLabel, icon }) => {
          const label = t(labelKey, { defaultValue: defaultLabel });
          return (
            <Grid size={{ xs: 6, sm: 3 }} key={id}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={icon}
                onClick={() => setOpenCategory(id)}
                sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
              >
                {label}
              </Button>
            </Grid>
          );
        })}
      </Grid>

      {openCategory && (
        <Dialog
          open={true}
          onClose={() => setOpenCategory(null)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { height: '80vh' } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {openCategoryTitle}
            <IconButton aria-label="close" onClick={() => setOpenCategory(null)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 0 }}>
            <PerformCategoryModalContent
              category={openCategory}
              flow={flow}
              selectedSlot={selectedSlot}
              entityRef={entityRef}
              eventSlotId={eventSlotId}
              livekitRoomId={livekitRoomId}
              liveContextState={liveContextState}
              onTrackSelected={() => setOpenCategory(null)}
            />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
}
